import { action, computed, observable, makeObservable } from 'mobx';
import { checkState } from '../../../utils/preconditions';
import { FieldSetType, UserFormState } from '../../common/userInfo/states/UserFormState';
import { scrollToTop } from '../../../utils/scroll';
import { translateErrorMessage } from '../../../api/error';
import { GoogleAnalyticsClient } from '../../../../commonv2/modules/analytics';
import { CardPaymentState } from '../../common/payment/states/CardPaymentState';
import { StepSpec } from '../../common/stepPage/StepIndicator';
import { RemoteResourceV2, updateRemoteResourceV2 } from '../../../../commonv2/api/RemoteResourceV2';
import moment from 'moment-timezone';
import { GlobalStates } from '../../../states/globalStates';
import { FieldState } from '../../../states/FormState';
import { EventDetails, EventId, UserEventReservationStatus } from '../../../types/event';
import { EventApis, EventReservationApis } from '../../../api/event';
import { CurrentUserState } from '../../../states/CurrentUserState';
import { TokenType } from '../../../../commonv3/types/tokens';
import { UserEventReservationApis } from '../../../api/user';

/* eslint-disable */
export enum ReserverType {
  NEW_USER = 0,
  OLD_USER = 1,
  USER = 2,
}

export enum PageStep {
  INPUT = 0,
  CONFIRMATION = 1,
  COMPLETE = 2,
  ERROR = 3,
}
/* eslint-enable */

export const EVENT_RESERVATION_STEPS: StepSpec[] = [
  { id: PageStep.INPUT, subTitle: 'Step. 1', title: '予約申し込み', visibleOnError: true },
  { id: PageStep.CONFIRMATION, subTitle: 'Step. 2', title: '内容の確認', visibleOnError: true },
  { id: PageStep.COMPLETE, subTitle: 'Step. 3', title: '予約完了', visibleOnError: false },
  { id: PageStep.ERROR, subTitle: 'Step. 3', title: '予約が失敗しました', error: true, visibleOnError: true },
];

export class EventReservationPageState {
  reserverType: ReserverType | null = null;
  eventId: EventId | null = null;
  event: RemoteResourceV2<EventDetails> = RemoteResourceV2.loading();
  currentStep: PageStep = PageStep.INPUT;
  userForm: UserFormState | null = null;
  reservationNote = new FieldState<string>('', false);
  paymentMethodForm: CardPaymentState = new CardPaymentState();
  processing = false;
  error = false;
  errorMessage?: string;
  loading = true;
  hasAffordableTokenForNow = false;
  hasAffordableTokenForEventStartAt = false;
  privacyPolicyAgreed = false;
  alreadyReserved = false;

  constructor() {
    makeObservable<EventReservationPageState, 'doReserve'>(this, {
      reserverType: observable,
      eventId: observable,
      event: observable,
      currentStep: observable,
      userForm: observable,
      reservationNote: observable,
      paymentMethodForm: observable,
      processing: observable,
      error: observable,
      errorMessage: observable,
      loading: observable,
      hasAffordableTokenForNow: observable,
      hasAffordableTokenForEventStartAt: observable,
      privacyPolicyAgreed: observable,
      alreadyReserved: observable,
      appliedPaymentType: computed,
      appliedPrice: computed,
      isIWS: computed,
      goToConfirmation: action.bound,
      goBackToInput: action.bound,
      goToErrorPage: action.bound,
      submit: action.bound,
      doReserve: action.bound,
      togglePrivacyPolicyAgreed: action.bound,
    });
  }

  get appliedPaymentType() {
    if (this.event.isEmpty()) {
      return 'credit_card';
    }
    return this.event.get().applied_payment_type;
  }

  get appliedPrice() {
    if (this.event.isEmpty()) {
      return 0;
    }
    return this.event.get().applied_price;
  }

  get userEventReservationStatus(): UserEventReservationStatus | null {
    if (this.event.isEmpty()) return null;
    const event = this.event.get();
    return {
      eventId: event.id,
      reserved: this.alreadyReserved,
      hasAffordableTokenForNow: this.hasAffordableTokenForNow,
      hasAffordableTokenForEventStartAt: this.hasAffordableTokenForEventStartAt,
    };
  }

  get isIWS(): boolean {
    // INNOVATIVE WOMEN'S SUMMIT のみ会社名と職種を必須にする
    // https://sheinc.atlassian.net/browse/SHE-5087
    // INNOVATIVE WOMEN'S SUMMIT のみ個別の利用規約へ同意を求める
    // https://sheinc.atlassian.net/browse/SHE-5084
    // INNOVATIVE WOMEN'S SUMMIT のみ応募者数の表示をフラグ制御にする
    // https://sheinc.atlassian.net/browse/SHE-5091
    if (['1581', '1573'].find((id) => this.eventId === Number(id))) return true;
    return false;
  }

  async init(eventId: EventId, userState: CurrentUserState) {
    this.loading = true;
    try {
      this.eventId = eventId;
      const event = await updateRemoteResourceV2(
        this.event,
        (v) => (this.event = v),
        () => EventApis.find(eventId)
      );
      await userState.reloadUser();

      // INNOVATIVE WOMEN'S SUMMIT のみ会社名と職種を必須にする
      // https://sheinc.atlassian.net/browse/SHE-5087
      const requiredFields = this.isIWS ? (['job_company', 'job_title'] as const) : ([] as const);
      this.userForm = new UserFormState(requiredFields);

      this.userForm.init(userState.currentUser, FieldSetType.EVENT);
      if (userState.isSignedIn()) {
        const userReservations = await UserEventReservationApis.findAll();
        this.alreadyReserved =
          userReservations.find((r) => r.event_type === 'Event' && r.event_id === this.eventId) !== undefined;
        if (event) {
          this.hasAffordableTokenForNow =
            userState.availableTokens.findAffordableToken(TokenType.EVENT, 1, moment()) != null;
          this.hasAffordableTokenForEventStartAt =
            userState.availableTokens.findAffordableToken(TokenType.EVENT, 1, event.starts_at) != null;
        }
        this.reserverType = ReserverType.USER;
        const card = await this.paymentMethodForm.loadOriginalCard();
        if (card) {
          this.paymentMethodForm.useNewCard = false;
        } else {
          this.paymentMethodForm.useNewCard = true;
        }
      } else {
        this.reserverType = ReserverType.NEW_USER;
      }
    } catch (e) {
      this.goToErrorPage(e);
    } finally {
      this.loading = false;
    }
  }

  isRequiredFieldsFilled() {
    return this.userForm && this.userForm.isRequiredFieldsFilled() && this.paymentMethodForm.isRequiredFieldsFilled();
  }

  async goToConfirmation() {
    checkState(this.currentStep === PageStep.INPUT);
    if (!this.userForm) {
      throw new Error('EventReservationPageState is not initialized');
    }
    this.processing = true;
    try {
      let valid = true;
      if (this.userForm.needUserInput()) {
        await this.userForm.validate();
        valid = !this.userForm.hasErrors();
      }
      if (this.paymentMethodForm.useNewCard && this.appliedPrice > 0) {
        await this.paymentMethodForm.validateCardInfo();
        valid = valid && this.paymentMethodForm.canSubmit();
      }
      if (valid) {
        this.currentStep = PageStep.CONFIRMATION;
      }
    } catch (e) {
      this.goToErrorPage(e);
    } finally {
      this.processing = false;
      scrollToTop();
    }
  }

  goBackToInput() {
    checkState(this.currentStep === PageStep.CONFIRMATION);
    this.currentStep = PageStep.INPUT;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  goToErrorPage(error: any) {
    this.error = true;
    this.errorMessage = translateErrorMessage(error);
    this.currentStep = PageStep.ERROR;
  }

  async submit() {
    checkState(this.currentStep === PageStep.CONFIRMATION);
    this.processing = true;
    try {
      await this.doReserve();
      await GlobalStates.currentUser.reloadUser();
      this.reportReservation();
      this.currentStep = PageStep.COMPLETE;
    } catch (e) {
      this.goToErrorPage(e);
    } finally {
      this.processing = false;
      scrollToTop();
    }
  }

  private reportReservation() {
    if (!this.userForm) return;
    const eventLabel = moment(this.event.get().starts_at).format('YYYYMMDD');
    const { currentUser } = GlobalStates.currentUser;
    const email = currentUser ? currentUser.email : this.userForm.getValue('email');
    GoogleAnalyticsClient.sendGaEvent('trial', 'event_reservation_complete', eventLabel, 0, {
      email: email,
    });
  }

  private async doReserve() {
    if (this.eventId === null) return;
    if (!this.userForm) {
      throw new Error('EventReservationPageState is not initialized');
    }
    const reservationNote = this.reservationNote.value;
    if (this.userForm.needUserInput()) {
      await EventReservationApis.reserveWithUserForm(
        this.eventId,
        this.appliedPaymentType,
        this.userForm.asJson(),
        this.paymentMethodForm.newCardToken,
        reservationNote || undefined
      );
    } else {
      await EventReservationApis.reserve(
        this.eventId,
        this.appliedPaymentType,
        this.paymentMethodForm.newCardToken,
        reservationNote || undefined
      );
    }
  }

  togglePrivacyPolicyAgreed() {
    this.privacyPolicyAgreed = !this.privacyPolicyAgreed;
  }
}
